import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    openVirtualAppointmentsNow,
    type OpenVirtualAppointmentsNowResult,
    type VirtualAppointmentOpenNowPlan
} from '../../services/virtualAppointments';
import { PATIENT_VIRTUAL_APPOINTMENTS_KEY } from '../../hooks/queryKeys';
import { apiErrorMessage } from '../../utils/apiError';

const OPEN_FALLBACK = "Couldn't open these VAs. Try again.";

export interface OpenVirtualAppointmentsNow {
    submitting: boolean;
    error: string | null;
    open: (
        plans: VirtualAppointmentOpenNowPlan[],
        onSuccess: (result: OpenVirtualAppointmentsNowResult) => void
    ) => void;
}

// Refreshes the patient's VAs (and their round) before reporting success, like adding one does.
export function useOpenVirtualAppointmentsNow(patientId: string): OpenVirtualAppointmentsNow {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: (plans: VirtualAppointmentOpenNowPlan[]) =>
            openVirtualAppointmentsNow(patientId, plans),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: PATIENT_VIRTUAL_APPOINTMENTS_KEY(patientId)
            })
    });
    const { mutate, isPending, isError, error } = mutation;
    const open = useCallback<OpenVirtualAppointmentsNow['open']>(
        (plans, onSuccess) => mutate(plans, { onSuccess }),
        [mutate]
    );
    const message = isError ? apiErrorMessage(error, OPEN_FALLBACK) : null;
    return useMemo(
        () => ({ submitting: isPending, error: message, open }),
        [isPending, message, open]
    );
}
